import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList } from 'lucide-react';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';
import { LoadingShell, ErrorState, EmptyState } from '@siliconx/shared/ui/state';
import type { BenchPlanSearchResult } from '@siliconx/shared';
import { qk } from '../../../lib/queryKeys';
import { useAuth } from '../../../lib/authContext';
import { getBenchMandatorySet, isPlansDisabled, searchBenchPlans } from '../../../lib/benchPlans';
import { areaLabel, orderAreas, ownerLabel } from '../../../components/bench/planModel';

/**
 * SIL-2246 U1. Bench plans of the org and the SiliconX templates. Any member reads; creating, editing and archiving is
 * org-admin only (the plan routes enforce it, this page hides the buttons). While the server switch BENCH_PLANS_ENABLED is
 * off the API answers 404/409 plans_disabled and this page says plans are not available.
 */
export const Route = createFileRoute('/bench/plans/')({
  component: BenchPlansView,
});

function PlanRow({ plan, isAdmin }: { plan: BenchPlanSearchResult; isAdmin: boolean }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/bench/plans/$planId" params={{ planId: plan.id }} className="font-medium hover:underline">
            {plan.name}
          </Link>
          <StatusPill tone={plan.origin === 'siliconx' ? 'neutral' : 'green'}>{ownerLabel(plan.origin)}</StatusPill>
          <span className="text-xs text-muted-foreground">v{plan.version}</span>
          {plan.updateAvailable && (
            <StatusPill tone="amber" title="SiliconX published a newer version of the template this plan was copied from.">
              Update available
            </StatusPill>
          )}
          {plan.includesWipe && <StatusPill tone="red">includes wipe</StatusPill>}
        </div>
        <p className="text-xs text-muted-foreground">
          In scope: {orderAreas(plan.areas).filter((a) => plan.areas.includes(a)).map(areaLabel).join(', ') || 'Inventory'}
        </p>
      </div>
      {isAdmin && plan.origin === 'siliconx' && (
        <Link
          to="/bench/plans/new"
          search={{ from: plan.id }}
          className="rounded-md border border-border bg-card px-3 py-1.5 text-xs hover:bg-muted"
        >
          Copy to my plans
        </Link>
      )}
    </li>
  );
}

export function BenchPlansView() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const plans = useQuery({ queryKey: qk.bench.planSearch('', ''), queryFn: () => searchBenchPlans(''), retry: false });
  const mandatory = useQuery({ queryKey: qk.bench.mandatorySet(), queryFn: getBenchMandatorySet, retry: false });

  if (plans.isError && isPlansDisabled(plans.error)) {
    return (
      <div className="space-y-6">
        <PageHeader title="Bench Plans" subtitle="Choose what a bench run tests." pinTo="/bench/plans" />
        <InsightBanner tone="neutral" lead="Plans are not available yet.">
          Bench runs test every device with the standard SiliconX profiles until plans are turned on for your organization.
        </InsightBanner>
      </div>
    );
  }

  const orgPlans = (plans.data ?? []).filter((p) => p.origin === 'org');
  const templates = (plans.data ?? []).filter((p) => p.origin === 'siliconx');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bench Plans"
        subtitle="A plan decides which hardware a bench run tests and what it records. SiliconX mandatory tests are always included."
        pinTo="/bench/plans"
        actions={
          isAdmin ? (
            <Link to="/bench/plans/new" search={{}} className="rounded-md bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700">
              New plan
            </Link>
          ) : undefined
        }
      />
      {plans.isPending && <LoadingShell rows={6} />}
      {plans.isError && <ErrorState error={plans.error} onRetry={() => void plans.refetch()} />}

      {mandatory.data && (
        <Card className="space-y-1">
          <PanelTitle
            title={`SiliconX mandatory rules, version ${mandatory.data.version}`}
            description={`Effective ${new Date(mandatory.data.effectiveAt).toISOString().slice(0, 10)}. Every plan includes these tests; they cannot be removed.`}
          />
          {mandatory.data.changeSummary && <p className="text-sm text-muted-foreground">Last change: {mandatory.data.changeSummary}</p>}
        </Card>
      )}

      {plans.data && (
        <>
          <Card className="space-y-2">
            <PanelTitle title="Your plans" />
            {orgPlans.length === 0 ? (
              <EmptyState
                icon={ClipboardList}
                title="No plans of your own yet"
                description={isAdmin ? 'Copy a SiliconX template below or start a blank plan.' : 'An org admin can create plans. Approvals use a SiliconX template until then.'}
              />
            ) : (
              <ul className="divide-y divide-border">{orgPlans.map((p) => <PlanRow key={p.id} plan={p} isAdmin={isAdmin} />)}</ul>
            )}
          </Card>
          <Card className="space-y-2">
            <PanelTitle title="SiliconX templates" />
            {templates.length === 0 ? (
              <p className="text-sm text-muted-foreground">No templates are published yet.</p>
            ) : (
              <ul className="divide-y divide-border">{templates.map((p) => <PlanRow key={p.id} plan={p} isAdmin={isAdmin} />)}</ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
